import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { Photo } from "types/Photo";
import './Lexend-VariableFont_wght-bold';
import './Lexend-VariableFont_wght-bolditalic';
import './Lexend-VariableFont_wght-italic';
import './Lexend-VariableFont_wght-normal';


export async function handleGeneratePdf(state: any, includeAll: boolean) {
    const doc = new jsPDF('p', 'mm', 'a4', true);
    // dimensions: [210, 297] // A4 size in mm
        
    
    const FONT = "Lexend-VariableFont_wght";
    const MARGIN = 20;
    


    const addHeader = () => {
        doc.addImage("/logo/logo-horizontal.png", 'PNG', (210 - 40) / 2, 10, 40, 9);

        doc.setFontSize(18);
        doc.setFont(FONT, 'bold');
        doc.setTextColor("#30A9E0");
        const title = state.form.getValues()["assetCode"] ? `Aperture Screen AI Report - ${state.form.getValues()["assetCode"]}` : "Aperture Screen AI Report";
        doc.text(`${title}`, 20, 30);
        doc.setTextColor("#000000");
        // draw line under title
        doc.setLineWidth(0.5);
        doc.line(20, 35, 210 - 20, 35);
        doc.setFontSize(12);
        doc.setFont(FONT, 'normal');

    }


    doc.setFont(FONT);
    addHeader();

    const details_start_y = 45;
    const detail_end_y = 95;
    const line_height = (detail_end_y - details_start_y) / 6;
    doc.setFontSize(12);
    doc.setFont(FONT, 'normal');
    doc.text(`Site Location: ${state.form.getValues()["location"]}`, MARGIN, details_start_y + line_height * 0);
    doc.text(`Mech Asset Code: ${state.form.getValues()["assetCode"]}`, MARGIN, details_start_y + line_height * 1);
    doc.text(`Inspector Name: ${state.form.getValues()["user"]}`, MARGIN, details_start_y + line_height * 2);
    // Format: Mon 30/10/2023 2:23 PM
    const now = new Date();
    const formattedDate = now.toLocaleDateString('en-GB', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
    const formattedTime = now.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,

    });
    doc.text(`Inspection Date: ${formattedDate} ${formattedTime}`, MARGIN, details_start_y + line_height * 3);
    doc.text(`Aperture Baseline Gap Width: ${state.form.getValues()["baselineWidth"]}`, MARGIN, details_start_y + line_height * 4);

    const comments_x_offset = doc.getTextWidth("Comments: ");
    const comments = doc.splitTextToSize(`${state.form.getValues()["comments"]}`, 210 - 2 * MARGIN - comments_x_offset);

    doc.text("Comments: ", MARGIN, details_start_y + line_height * 5);
    doc.text(comments, MARGIN + comments_x_offset, details_start_y + line_height * 5);

    const comments_height = comments.length * 6; // approx line height 7mm


    let yOffset = detail_end_y + comments_height;
    for (const [index, photoEntry] of state.photos.entries()) {
        const photo: Photo = photoEntry;
    

        if (includeAll || (photo.state === "active" && photo.src)) {

            // Convert first so this works for remote (presigned S3) URLs on a
            // restored session, not just same-origin blob URLs. getImageProperties
            // needs the actual image data, and we reuse the base64 below.
            const rawBase64 = await urlToBase64(photo.src);
            const imgProps = doc.getImageProperties(rawBase64);
            var imgWidth = 70;
            var imgHeight = 0;

            // If portrait, height is scaled based on width
            if (photo.orientation === "portrait") {
                imgHeight = (imgProps.width / imgProps.height) * imgWidth;
            } else {
                imgHeight = (imgProps.height / imgProps.width) * imgWidth;
            }

            
            if (yOffset + imgHeight + 20 > doc.internal.pageSize.getHeight()) {
                doc.addPage();
                addHeader();
                yOffset = 50;
            }
            doc.text(`Photo ${index + 1}: ${photo.label}`, MARGIN, yOffset);
            yOffset += 5;


            const compressed_img = await resizeImage(rawBase64);
            doc.addImage(compressed_img, 'JPEG', MARGIN, yOffset, imgWidth, imgHeight, undefined, 'MEDIUM');
            if (photo.state === "active" && photo.annotatedSrc) {
                const bs64 = await urlToBase64(photo.annotatedSrc);
                const compressed_img = await resizeImage(bs64);
    
                doc.addImage(compressed_img, 'JPEG', MARGIN + imgWidth + 20, yOffset, imgWidth, imgHeight, undefined, 'MEDIUM');
            }
            yOffset += imgHeight + 20;
        }
    };


    const precision = (num: number, precision: number = 0.05) => {
        return Math.round(num / precision) * precision;
    }

    // widths is now an n-by-m grid with null for empty cells. Flatten and drop
    // the empty cells to get the flat list of readings the stats expect.
    const flattenWidths = (w?: (number | null)[][]): number[] =>
        (w ?? []).flat().filter((v): v is number => v != null);

    var data: any[] = [];
    state.photos.forEach((photo: Photo, index: number) => {
        const widths = flattenWidths(photo.widths);
        if (photo.state === "active" && widths.length > 0) {
            const numReadings = widths.length;
            const sortedWidths = widths.sort((a, b) => a - b);

            const min = precision(sortedWidths[0]);
            const max = precision(sortedWidths[sortedWidths.length - 1]);
            const avg = precision(sortedWidths.reduce((a, b) => a + b, 0) / sortedWidths.length);
            const p25 = precision(sortedWidths[Math.floor(0.25 * (sortedWidths.length - 1))]);
            const p75 = precision(sortedWidths[Math.floor(0.75 * (sortedWidths.length - 1))]);
            data.push({ 1: `Photo ${index + 1}`, 2: numReadings, 3: avg.toFixed(2).toString(), 4: min.toFixed(2).toString(), 5: max.toFixed(2).toString(), 6: p25.toFixed(2).toString(), 7: p75.toFixed(2).toString() });
        }

    })
    var all_widths = state.photos
        .filter((photo: Photo) => photo.state === "active")
        .flatMap((photo: Photo) => flattenWidths(photo.widths));
    all_widths.sort((a: number, b: number) => a - b);
    const data_agg = {
        1: 'Overall',
        2: all_widths.length,
        3: `${precision((all_widths.reduce((a: number, b: number) => a + b, 0) / all_widths.length)).toFixed(2)}`,
        4: `${precision(all_widths[0]).toFixed(2)}`,
        5: `${precision(all_widths[all_widths.length - 1]).toFixed(2)}`,
        6: `${precision(all_widths[Math.floor(0.25 * (all_widths.length - 1))]).toFixed(2)}`,
        7: `${precision(all_widths[Math.floor(0.75 * (all_widths.length - 1))]).toFixed(2)}`
    };



    const createTable = (doc: jsPDF, data: any[], data_agg: any, startY: number) => {
        var final_table_height = 0;
        autoTable(doc, {
            startY: startY,
            head: [[
                'Photo',
                'Number of Readings',
                'Average (mm)',
                'Min (mm)',
                'Max (mm)',
                'Q1 (mm)',
                'Q3 (mm)'
            ]],
            body: data.map(item => [
                item[1],
                item[2],
                item[3],
                item[4],
                item[5],
                item[6],
                item[7]
            ]),
            foot: [['Overall',
                data_agg[2],
                data_agg[3],
                data_agg[4],
                data_agg[5],
                data_agg[6],
                data_agg[7]
            ]],
            margin: { left: MARGIN, right: MARGIN },
            styles: { font: FONT, fontSize: 10 },
            headStyles: { fillColor: "#30A9E0" },
            footStyles: { fillColor: "#CCCCCC" },
            didDrawPage: function (data) {
                final_table_height = data.cursor?.y ?? final_table_height;
            },
        });
        return final_table_height;
    }

    const tempDoc = new jsPDF('p', 'mm', 'a4', true);
    let table_height = createTable(tempDoc, data, data_agg, 0);

    if (yOffset + table_height + 20 > doc.internal.pageSize.getHeight()) {
        console.log(`Adding new page for table, because ${yOffset} + ${table_height} + 20 > ${doc.internal.pageSize.getHeight()}`);
        doc.addPage();
        addHeader();
        yOffset = 50;
    }
    table_height = createTable(doc, data, data_agg, yOffset);

    // Add caption below table
    doc.setFontSize(8);
    doc.setFont(FONT, 'italic'); // Italic not working
    doc.text("Readings given at 0.05mm precision", MARGIN, table_height + 5);

    const timestamp = now.toISOString().replace(/[:]/g, '-').split('.')[0];
    doc.save('Screen_AI_Report_' + (state.form.getValues()["assetCode"] ? state.form.getValues()["assetCode"] + '_' : '') + timestamp + '.pdf');

    return;
};


async function resizeImage(base64: any, maxWidth = 800, quality = 0.7): Promise<string> {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const scale = Math.min(1, maxWidth / img.width);
            const canvas = document.createElement('canvas');
            canvas.width = img.width * scale;
            canvas.height = img.height * scale;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.src = base64;
    });
}


async function urlToBase64(url: string): Promise<string> {
    const response = await fetch(url);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}